<template>
    <div
        class="pl-3 pr-9 py-3 white"
        style="border-radius: 8px"
    >
        <input
            id="taskEmailAttachment"
            ref="taskEmailAttachment"
            class="d-none"
            type="file"
            multiple
            name="taskEmailAttachment"
            @change="uploadChange"
        />
        <v-row no-gutters>
            <v-col class="shrink mr-1">
                <v-icon
                    color="primary"
                    size="21"
                >
                    mdi-email-outline
                </v-icon>
            </v-col>
            <v-col>
                <div>
                    <div
                        class="font-weight-bold body-2 mb-4"
                        style="padding-left: 7px; padding-top: 2px;"
                    >
                        {{ $t('Send e-mail') }}
                    </div>
                    <Email
                        ref="emailBlock"
                        :to-recipients="toRecipients"
                        :cc-recipients="ccRecipients"
                        :bcc-recipients="bccRecipients"
                        :content="content"
                        :subject="subject"
                        :files="form_data.files"
                        :contact-tabs="contactTabs"
                        :errors="errors"
                        :template-id="templateId"
                        :only-automations="false"
                        :account-id="accountId"
                        :selected-locale-code="localeCode"
                        :is-cc-active="isCcActive"
                        :is-bcc-active="isBccActive"
                        :client-id="clientId"
                        :email-integration-id="emailIntegrationId"
                        toolbar-id="#client-email-toolbar"
                        @update-email-integration-id="emailIntegrationId = $event"
                        @clearTemplate="clearTemplate"
                        @updateContent="updateContent"
                        @updateSubject="updateSubject"
                        @updateToRecipients="updateToRecipients"
                        @updateCcRecipients="updateCcRecipients"
                        @updateBccRecipients="updateBccRecipients"
                        @deleteFile="fileUpload.delete_file"
                        @toggleCc="isCcActive = !isCcActive"
                        @toggleBcc="isBccActive = !isBccActive"
                        @focusInfo="setFocusedField($event)"
                    />
                    <Footer
                        :rich-text-editor="richTextEditor"
                        :is-automation="isAutomation"
                        :is-template="!!templateId"
                        :is-new-email="isNewEmail"
                        :placeholder-types="placeholderTypes()"
                        :company-id="accountId"
                        :can-add-automation="false"
                        toolbar-id="client-email-toolbar"
                        @chooseTemplate="showChooseTemplateDialog = true"
                        @addAttachment="taskEmailAttachment.click()"
                        @addAutomation="isAutomation = true"
                        @close="close"
                        @send="send"
                        @placeholder="addPlaceholder($event)"
                        @placeholders="setAutoSignature($event)"
                    />
                    <TemplateChooseDialog
                        :show-dialog="showChooseTemplateDialog"
                        :account-id="accountId"
                        :client-locale-code="clientLocaleCode"
                        @close="showChooseTemplateDialog = false"
                        @templateChosen="onTemplateChosen"
                        @templateDeleted="onTemplateDeleted"
                        @templateUpdated="onTemplateUpdated"
                    />
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { $t } from "@/i18n";
import { computed, ref, watch } from "@vue/composition-api";
import Email from "@/components/Emailing/EmailEdit/Email"
import Footer from "@/components/Emailing/EmailEdit/Footer"
import PersonAvatar from "@/components/PersonAvatar.vue";
import RichTextEditor from "@/components/RichTextEditor/RichTextEditor";
import RichTextEditorToolbar from "@/components/RichTextEditor/RichTextEditorToolbar";
import RichTextEditorPlaceholders from "@/components/RichTextEditor/RichTextEditorPlaceholders";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import FileUpload from "@/composables/FileUpload";
import TemplateChooseDialog from "@/components/Emailing/EmailEdit/TemplateChooseDialog";
import Automation from "@/components/Emailing/EmailEdit/Automation";
import { deepClone } from "@/helpers/helpers";
import {EMAIL_AUTOMATION_PLACEHOLDER_TYPES, TASK_AUTOMATION_TRIGGER_TYPE} from "@/helpers/constants";
import EmailAutomationModule from "@/store/modules/email_automation";
import TaskAutomationModule from "@/modules/emailing/taskAutomation";
import ValidationModule from "@/modules/validation/validation"
import EmailAutomationValidationModule from "@/modules/emailing/emailAutomationValidation";
import FeedbackNotificationModule from "@/store/modules/feedback_notification";
import EmailSignatureModule from "../../../../api/email_automation/email_signature";
import ApiEmailAutomationPlaceholders from "../../../../api/email_automation/placeholders";

export default {
    name: "TaskAddEmailControl",
    components: {
        RichTextEditorPlaceholders,
        RichTextEditorToolbar,
        RichTextEditor,
        PersonAvatar,
        Email,
        Footer,
        TemplateChooseDialog,
        Automation,
    },
    props: {
        taskId: {
            type: [Number, String],
            default: null
        },
        accountId: {
            type: Number,
            required: true
        },
        clientId: {
            type: Number,
            default: null
        },
        projectId: {
            type: Number,
            default: null
        },
        contactTabs: {
            type: Array,
            required: true
        },
        automations: {
            type: Array,
            required: true
        },
        editingAutomationData: {
            type: Object,
            default: null
        },
        templates: {
            type: Array,
            required: true
        },
        clientLocaleCode: {
            type: String,
            default: null
        }
    },
    setup(props, ctx) {
        const { showFeedbackNotification } = FeedbackNotificationModule();
        const { fetchEmailAutomationTemplates } = EmailAutomationModule()
        const {getMemberDefaultSignature} = EmailSignatureModule()
        const task_data = ref({
            account_id: props.accountId,
            id: props.taskId,
            files: []
        })
        const form_data = ref({
            account_id: props.accountId,
            id: props.taskId,
            files: []
        })
        const selectedDays = ref(0);
        const selectedTrigger = ref(TASK_AUTOMATION_TRIGGER_TYPE.START_DATE);
        const showChooseTemplateDialog = ref(false);
        const taskEmailAttachment = ref(null);
        const emailBlock = ref(null);
        const isCcActive = ref(false);
        const isBccActive = ref(false);
        const errors = ref({});
        const toRecipients = ref([]);
        const ccRecipients = ref([]);
        const bccRecipients = ref([]);
        const subject = ref("");
        const content = ref("");
        const isAutomation = ref(false);
        const templateId = ref(null);
        const localeCode = ref(null);
        const emailIntegrationId = ref(null);
        const isNewEmail = ref(true);
        const focusInfo = ref({fieldName: "content", cursorIndex: null})
        const autoSignatureAdded = ref(false)
        const { getPlaceholderTypes } = ApiEmailAutomationPlaceholders

        const richTextEditor = computed(() => {
            if (emailBlock.value) {
                return emailBlock.value.$refs.richTextEditor
            }
        })

        const close = () => {
            ctx.emit('close')
        }

        const validateInput = () => {
            const validatedErrors = {
                subject: subject.value ? null : $t('Required field'),
                content: content.value ? null : $t('Required field'),
                toRecipients: toRecipients.value.length > 0 ? null : $t('Required field'),
                toRecipientsValues: EmailAutomationValidationModule.validateRecipients(toRecipients.value),
                ccRecipientsValues: EmailAutomationValidationModule.validateRecipients(ccRecipients.value),
                bccRecipientsValues: EmailAutomationValidationModule.validateRecipients(bccRecipients.value)
            }

            errors.value = validatedErrors
        }

        const send = async () => {
            setTimeout(() => {
                errors.value = ValidationModule.clearErrors(errors.value)

                validateInput()
                if (ValidationModule.hasErrors(errors.value)) {
                    return
                }

                const saveData = TaskAutomationModule.getSaveData(
                    isAutomation.value,
                    selectedDays.value,
                    selectedTrigger.value,
                    props.editingAutomationData ? props.editingAutomationData.id : null,
                    templateId.value,
                    subject.value,
                    content.value,
                    form_data.value.files,
                    localeCode.value,
                    emailIntegrationId.value,
                    toRecipients.value,
                    ccRecipients.value,
                    bccRecipients.value
                )

                resetData()
                ctx.emit('saveAutomation', saveData)
                close()
            }, 0)
        }

        const fileDeleted = (deletedId) => {
            form_data.value.files = form_data.value.files.filter(x => x.id !== deletedId);
            task_data.value.files = task_data.value.files.filter(x => x.id !== deletedId);
        }
        const fileUpload = FileUpload("email_task", task_data, form_data, false, null, fileDeleted);
        const uploadChange = (e) => {
            fileUpload.add_files_to_upload(e.target.files, false);
        };

        const updateContent = (e) => {
            content.value = e
            errors.value = ValidationModule.clearError(errors.value, 'content')
        }

        const updateSubject = (e) => {
            subject.value = e;
            errors.value = ValidationModule.clearError(errors.value, 'subject')
        }

        const getTemplateRow = (editingAutomation) => {
            const template = props.templates.find(x => x.id === editingAutomation.content_template_id)
            if (!template) {
                return
            }

            return template.rows.find(x => x.locale_code === editingAutomation.locale_code)
        }

        const onTemplateChosen = (template) => {
            subject.value = template.subject
            content.value = template.content
            templateId.value = template.templateId
            localeCode.value = template.locale_code
            showChooseTemplateDialog.value = false
        }

        const loadEditingEmail = async () => {
            const editingAutomation = deepClone(props.editingAutomationData)

            templateId.value = editingAutomation.content_template_id
            subject.value = editingAutomation.subject ? editingAutomation.subject : ''
            content.value = editingAutomation.content ? editingAutomation.content : ''

            if (editingAutomation.content_template_id) {
                localeCode.value = editingAutomation.locale_code;

                const templateRow = getTemplateRow(editingAutomation)
                if (templateRow) {
                    subject.value = templateRow.subject
                    content.value = templateRow.content
                }
            }

            const recipients = TaskAutomationModule.addNameToRecipientsWithContact(editingAutomation.data.recipients, props.contactTabs)

            emailIntegrationId.value = editingAutomation.is_email_integration_client_default ? -1 : editingAutomation.email_integration_id
            toRecipients.value = recipients.filter(x => x.type === 'to')
            ccRecipients.value = recipients.filter(x => x.type === 'cc')
            bccRecipients.value = recipients.filter(x => x.type === 'bcc')
            task_data.value.files = editingAutomation.attachments ? editingAutomation.attachments : []
            form_data.value.files = editingAutomation.attachments ? editingAutomation.attachments : []

            selectedDays.value = editingAutomation.days;
            selectedTrigger.value = editingAutomation.trigger;

            if (selectedTrigger.value && selectedTrigger.value !== '') {
                isAutomation.value = true
            }

            isNewEmail.value = false

            if (ccRecipients.value.length > 0) {
                isCcActive.value = true
            }

            if (bccRecipients.value.length > 0) {
                isBccActive.value = true
            }

            try {
                await fetchEmailAutomationTemplates(props.accountId)
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const updateToRecipients = (e) => {
            toRecipients.value = e
            errors.value.toRecipients = null;
            errors.value.toRecipientsValues = null

        }

        const updateCcRecipients = (e) => {
            ccRecipients.value = e
            errors.value.ccRecipientsValues = null
        }

        const updateBccRecipients = (e) => {
            bccRecipients.value = e
            errors.value.bccRecipientsValues = null
        }

        if (props.editingAutomationData !== null) {
            loadEditingEmail()
        }

        const onTemplateDeleted = (template) => {
            if (template.id === templateId.value) {
                templateId.value = null
            }
        }

        const onTemplateUpdated = (template) => {
            if (template.id === templateId.value) {
                const row = template.rows.find(x => x.locale_code === localeCode.value)
                if (row && row.content && row.subject) {
                    content.value = row.content
                    subject.value = row.subject
                } else {
                    templateId.value = null
                }
            }
        }

        const clearTemplate = () => {
            templateId.value = null
            showFeedbackNotification($t("Template unlinked"))
        }

        const resetData = () => {
            selectedDays.value = 0;
            selectedTrigger.value = TASK_AUTOMATION_TRIGGER_TYPE.START_DATE;
            showChooseTemplateDialog.value = false;
            taskEmailAttachment.value = null;
            emailBlock.value = null;
            isCcActive.value = false;
            isBccActive.value = false;
            errors.value = {};
            toRecipients.value = [];
            ccRecipients.value = [];
            bccRecipients.value = [];
            subject.value = "";
            content.value = "";
            isAutomation.value = false;
            templateId.value = null;
            localeCode.value = null
            isNewEmail.value = true;

            task_data.value = {
                account_id: props.accountId,
                id: props.taskId,
                files: []
            }
            form_data.value = {
                account_id: props.accountId,
                id: props.taskId,
                files: []
            }
        }

        const setFocusedField = (event) => {
            focusInfo.value = event
        }

        const placeholderTypes = () => {
            const types = getPlaceholderTypes("client_project_default")
            if (props.projectId) {
                types.push(EMAIL_AUTOMATION_PLACEHOLDER_TYPES.PROJECT)
            }

            return types
        }

        const addPlaceholder = (placeholder) => {
            if (!focusInfo.value) {
                return
            }
            const fieldName = focusInfo.value?.fieldName
            if (fieldName === "content") {
                let quill = richTextEditor.value.$refs.richTextEditorContent.quill
                let selection = quill.getSelection(true)
                quill.insertText(selection.index, placeholder.value)
            }
            else if (fieldName === "subject") {
                const cursorIndex = focusInfo.value.cursorIndex
                const firstPart = subject.value.slice(0, cursorIndex);
                const secondPart = subject.value.slice(cursorIndex);
                subject.value = firstPart + placeholder.value + secondPart

            }
        }

        const setAutoSignature = (placeholders) => {
            if (!isNewEmail.value || autoSignatureAdded.value) {
                return
            }

            const signature_member_default = getMemberDefaultSignature(placeholders)
            if (signature_member_default && signature_member_default.value) {
                // set
                let quill = richTextEditor.value.$refs.richTextEditorContent.quill
                insertNewLinesToQuill(quill)
                quill.insertText(3, signature_member_default.value);
                autoSignatureAdded.value = true
            }
        }

        const insertNewLinesToQuill = (quill) => {
            const editorContent = quill.getText();

            const lines = editorContent.split('\n');
            const linesToAdd = 3 - lines.length

            for (let i = 0; i < linesToAdd; i++) {
                quill.insertText(0, '\n', 'user')
            }
        }

        watch (() => props.editingAutomationData, () => {
            if (props.editingAutomationData) {
                loadEditingEmail()
            } else {
                resetData()
            }
        }, { immediate: true })

        return {
            emailIntegrationId,
            isCcActive,
            isBccActive,
            emailBlock,
            richTextEditor,
            close,
            send,
            toRecipients,
            ccRecipients,
            bccRecipients,
            subject,
            content,
            uploadChange,
            taskEmailAttachment,
            task_data,
            form_data,
            fileUpload,
            errors,
            updateContent,
            updateSubject,
            onTemplateChosen,
            showChooseTemplateDialog,
            isAutomation,
            templateId,
            selectedDays,
            selectedTrigger,
            updateToRecipients,
            localeCode,
            onTemplateDeleted,
            onTemplateUpdated,
            clearTemplate,
            isNewEmail,
            updateCcRecipients,
            updateBccRecipients,
            placeholderTypes,
            addPlaceholder,
            setFocusedField,
            setAutoSignature
        };
    }
};
</script>
